import type {
  Bible,
  Branch,
  Character,
  Knowledge,
  Moment,
  Relationship,
  ResolvedCharacterState,
  Secret,
  StatePatch,
  StoryEvent,
} from "./types";
import { STATE_PATCH_FIELDS } from "./types";

export function getBook(bible: Bible, bookId: string) {
  return bible.books.find((b) => b.id === bookId);
}

export function getMoment(bible: Bible, momentId: string | null): Moment | undefined {
  if (!momentId) return undefined;
  return bible.moments.find((m) => m.id === momentId);
}

export function getCharacter(bible: Bible, characterId: string) {
  return bible.characters.find((c) => c.id === characterId);
}

export function getBranch(bible: Bible, branchId: string) {
  return bible.branches.find((b) => b.id === branchId);
}

export function bookNumber(bible: Bible, bookId: string): number {
  return getBook(bible, bookId)?.number ?? 0;
}

export function momentSortKey(
  bible: Bible,
  moment: Moment,
  mode: "reading" | "story",
): number[] {
  const book = bookNumber(bible, moment.bookId);
  if (mode === "reading") {
    return [moment.readingOrder, book, moment.chapterNumber];
  }
  const story = moment.storyOrder ?? moment.readingOrder;
  return [book, story, moment.chapterNumber, moment.readingOrder];
}

export function compareKeys(a: number[], b: number[]): number {
  const n = Math.max(a.length, b.length);
  for (let i = 0; i < n; i++) {
    const d = (a[i] ?? 0) - (b[i] ?? 0);
    if (d !== 0) return d;
  }
  return 0;
}

export function compareMoments(
  bible: Bible,
  a: Moment,
  b: Moment,
  mode: "reading" | "story",
): number {
  const byKey = compareKeys(momentSortKey(bible, a, mode), momentSortKey(bible, b, mode));
  if (byKey !== 0) return byKey;
  return a.id.localeCompare(b.id);
}

export function sortedMoments(bible: Bible, mode: "reading" | "story"): Moment[] {
  return [...bible.moments].sort((a, b) => compareMoments(bible, a, b, mode));
}

export function isAtOrBefore(
  bible: Bible,
  fromId: string,
  targetId: string,
  mode: "reading" | "story" = "story",
): boolean {
  const from = getMoment(bible, fromId);
  const target = getMoment(bible, targetId);
  if (!from || !target) return false;
  return compareMoments(bible, from, target, mode) <= 0;
}

export function isBefore(
  bible: Bible,
  fromId: string,
  targetId: string,
  mode: "reading" | "story" = "story",
): boolean {
  const from = getMoment(bible, fromId);
  const target = getMoment(bible, targetId);
  if (!from || !target) return false;
  return compareMoments(bible, from, target, mode) < 0;
}

/** Chemin [soi, parent, ..., racine] */
export function getBranchPath(bible: Bible, branchId: string): string[] {
  const path: string[] = [];
  const seen = new Set<string>();
  let current: string | null | undefined = branchId;
  while (current && !seen.has(current)) {
    seen.add(current);
    path.push(current);
    const branch: Branch | undefined = getBranch(bible, current);
    current = branch?.parentBranchId ?? null;
  }
  return path;
}

export function branchDepth(bible: Bible, branchId: string): number {
  return Math.max(0, getBranchPath(bible, branchId).length - 1);
}

export function branchCompatible(
  bible: Bible,
  patchBranchId: string,
  targetBranchId: string,
): boolean {
  return getBranchPath(bible, targetBranchId).includes(patchBranchId);
}

export function isIntervalActive(
  bible: Bible,
  validFromMomentId: string,
  validToMomentId: string | null | undefined,
  targetMomentId: string,
): boolean {
  if (!isAtOrBefore(bible, validFromMomentId, targetMomentId, "story")) return false;
  if (!validToMomentId) return true;
  return isAtOrBefore(bible, targetMomentId, validToMomentId, "story");
}

export function isPatchActiveAt(
  bible: Bible,
  patch: Pick<StatePatch, "validFromMomentId" | "validToMomentId" | "branchId" | "loopIterationId">,
  target: Moment,
): boolean {
  if (!branchCompatible(bible, patch.branchId, target.branchId)) return false;
  if (patch.loopIterationId != null && patch.loopIterationId !== (target.loopIterationId ?? null)) {
    return false;
  }
  return isIntervalActive(
    bible,
    patch.validFromMomentId,
    patch.validToMomentId,
    target.id,
  );
}

function applyDefinedFields(
  state: ResolvedCharacterState,
  patch: StatePatch,
): ResolvedCharacterState {
  const next = { ...state, appliedPatchIds: [...state.appliedPatchIds, patch.id] };
  for (const key of STATE_PATCH_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(patch, key) && patch[key] !== undefined) {
      (next as Record<string, unknown>)[key] = patch[key];
    }
  }
  return next;
}

export function getBaseCharacterState(
  character: Character,
  moment: Moment | null,
): ResolvedCharacterState {
  return {
    characterId: character.id,
    momentId: moment?.id ?? null,
    branchId: moment?.branchId ?? null,
    loopIterationId: moment?.loopIterationId ?? null,
    isGeneralView: moment == null,
    className: character.className,
    lifeStatus: "alive",
    appliedPatchIds: [],
  };
}

export function resolveCharacterState(
  bible: Bible,
  characterId: string,
  momentId: string | null,
): ResolvedCharacterState | null {
  const character = getCharacter(bible, characterId);
  if (!character) return null;
  const moment = getMoment(bible, momentId);
  if (!moment) {
    return getBaseCharacterState(character, null);
  }

  const candidates = character.states
    .filter((patch) => isPatchActiveAt(bible, patch, moment))
    .sort((a, b) => {
      const depth = branchDepth(bible, a.branchId) - branchDepth(bible, b.branchId);
      if (depth !== 0) return depth;
      const fromA = getMoment(bible, a.validFromMomentId);
      const fromB = getMoment(bible, b.validFromMomentId);
      if (fromA && fromB) {
        const byStory = compareMoments(bible, fromA, fromB, "story");
        if (byStory !== 0) return byStory;
      }
      return (a.id || "").localeCompare(b.id);
    });

  return candidates.reduce(applyDefinedFields, getBaseCharacterState(character, moment));
}

export function characterTrajectory(bible: Bible, characterId: string): StatePatch[] {
  const character = getCharacter(bible, characterId);
  if (!character) return [];
  return [...character.states].sort((a, b) => {
    const fromA = getMoment(bible, a.validFromMomentId);
    const fromB = getMoment(bible, b.validFromMomentId);
    if (fromA && fromB) return compareMoments(bible, fromA, fromB, "story");
    return a.validFromMomentId.localeCompare(b.validFromMomentId);
  });
}

export function relationshipsAt(
  bible: Bible,
  momentId: string | null,
  characterId?: string,
): Relationship[] {
  const moment = getMoment(bible, momentId);
  return bible.relationships.filter((rel) => {
    if (characterId && rel.fromCharacterId !== characterId && rel.toCharacterId !== characterId) {
      return false;
    }
    if (!moment) return true;
    if (!branchCompatible(bible, rel.branchId, moment.branchId)) return false;
    return isIntervalActive(bible, rel.validFromMomentId, rel.validToMomentId, moment.id);
  });
}

export function eventsVisibleAt(
  bible: Bible,
  momentId: string | null,
  opts?: { bookId?: string | null },
): StoryEvent[] {
  const moment = getMoment(bible, momentId);
  return bible.events.filter((event) => {
    const eventMoment = getMoment(bible, event.momentId);
    if (opts?.bookId && eventMoment && eventMoment.bookId !== opts.bookId) return false;
    if (!moment) return true;
    if (!eventMoment) return false;
    if (!branchCompatible(bible, event.branchId, moment.branchId)) return false;
    if (
      eventMoment.loopIterationId != null &&
      moment.loopIterationId != null &&
      eventMoment.loopIterationId !== moment.loopIterationId
    ) {
      return false;
    }
    return isAtOrBefore(bible, event.momentId, moment.id, "story");
  });
}

export function eventsAtMoment(bible: Bible, momentId: string): StoryEvent[] {
  const moment = getMoment(bible, momentId);
  if (!moment) return [];
  return bible.events.filter((event) => {
    if (event.momentId !== moment.id) return false;
    return branchCompatible(bible, event.branchId, moment.branchId);
  });
}

export function isSecretObjectivelyTrue(
  bible: Bible,
  secret: Secret,
  momentId: string | null,
): boolean {
  if (!momentId) return true;
  if (!branchCompatible(bible, secret.branchId, getMoment(bible, momentId)?.branchId ?? secret.branchId)) {
    const moment = getMoment(bible, momentId);
    if (moment && !branchCompatible(bible, secret.branchId, moment.branchId)) return false;
  }
  return isAtOrBefore(bible, secret.trueFromMomentId, momentId, "story");
}

export function isRevealedToReader(
  bible: Bible,
  secret: Secret,
  momentId: string | null,
): boolean {
  if (!secret.revealedToReaderAtMomentId) return false;
  if (!momentId) return false;
  return isAtOrBefore(bible, secret.revealedToReaderAtMomentId, momentId, "story");
}

export function characterKnows(
  bible: Bible,
  secret: Secret,
  characterId: string,
  momentId: string | null,
): Knowledge | null {
  const entries = secret.knownBy.filter((k) => k.characterId === characterId);
  if (!momentId) return entries[0] ?? null;
  const moment = getMoment(bible, momentId);
  for (const k of entries) {
    if (k.branchId && moment && !branchCompatible(bible, k.branchId, moment.branchId)) continue;
    if (!isAtOrBefore(bible, k.knownFromMomentId, momentId, "story")) continue;
    if (k.knownToMomentId && isBefore(bible, k.knownToMomentId, momentId, "story")) continue;
    return k;
  }
  return null;
}

export function secretsForView(
  bible: Bible,
  momentId: string | null,
  viewer: "author" | "reader",
): Array<{
  secret: Secret;
  objectivelyTrue: boolean;
  readerKnows: boolean;
  hiddenFromReader: boolean;
}> {
  return bible.secrets.map((secret) => {
    const objectivelyTrue = isSecretObjectivelyTrue(bible, secret, momentId);
    const readerKnows = isRevealedToReader(bible, secret, momentId);
    const hiddenFromReader = objectivelyTrue && !readerKnows;
    if (viewer === "reader" && momentId && !readerKnows) {
      return { secret, objectivelyTrue, readerKnows: false, hiddenFromReader: true };
    }
    return { secret, objectivelyTrue, readerKnows, hiddenFromReader };
  });
}

export function momentsForChapter(bible: Bible, bookId: string, chapterNumber: number): Moment[] {
  return bible.moments.filter((m) => m.bookId === bookId && m.chapterNumber === chapterNumber);
}

export function adjacentMoment(
  bible: Bible,
  momentId: string,
  direction: -1 | 1,
  mode: "reading" | "story",
): Moment | null {
  const current = getMoment(bible, momentId);
  if (!current) return null;
  const list = sortedMoments(bible, mode).filter((m) => m.branchId === current.branchId);
  const pool = list.length > 1 ? list : sortedMoments(bible, mode);
  const idx = pool.findIndex((m) => m.id === momentId);
  if (idx < 0) return null;
  return pool[idx + direction] ?? null;
}

export function centralCharacterIds(): string[] {
  return ["alinoe", "lena", "bjorn", "axel", "liv", "ester", "senna", "magda", "jacob"];
}
